import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api/client';
import { Login } from './pages/Login';
import { Layout } from './components/Layout';
import { Spinner } from '@pocketsurge/ui';
import type { PublicAdmin } from '@pocketsurge/shared/types';

// Route-level code splitting — the editor (dompurify, diff view) and upload
// pages are the heaviest and are only needed on their routes.
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Articles = lazy(() => import('./pages/Articles').then((m) => ({ default: m.Articles })));
const ArticleEditor = lazy(() =>
  import('./pages/ArticleEditor').then((m) => ({ default: m.ArticleEditor }))
);
const Upload = lazy(() => import('./pages/Upload').then((m) => ({ default: m.Upload })));
const Blueprints = lazy(() => import('./pages/Blueprints').then((m) => ({ default: m.Blueprints })));
const Categories = lazy(() => import('./pages/Categories').then((m) => ({ default: m.Categories })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Logs = lazy(() => import('./pages/Logs').then((m) => ({ default: m.Logs })));
const ContentHealth = lazy(() =>
  import('./pages/ContentHealth').then((m) => ({ default: m.ContentHealth }))
);

function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get<{ admin: PublicAdmin }>('/admin/me');
      return data.admin;
    },
    staleTime: 5 * 60 * 1000,
  });
}

function Protected() {
  const { data, isLoading, isError } = useMe();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  if (isError || !data) return <Navigate to="/login" replace />;

  return <Layout admin={data} />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected />}>
        <Route
          element={
            <Suspense
              fallback={
                <div className="flex min-h-[40vh] items-center justify-center">
                  <Spinner className="h-8 w-8 text-indigo-600" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          }
        >
        <Route path="/" element={<Dashboard />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/new" element={<ArticleEditor />} />
        <Route path="/articles/:id/edit" element={<ArticleEditor />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/blueprints" element={<Blueprints />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/content-health" element={<ContentHealth />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
